import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { useUser } from "@/components/UserProvider";
import {
  ActionConfirmationModal,
  Button,
  Command,
  CommandItem,
  CommandList,
  Form,
  FormControl,
  FormField,
  FormItem,
  InlineEditableField,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Input,
  Skeleton,
  Typography,
} from "@/components/ui";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { useOnboardingStepFrameScroll } from "@/components/onboarding/OnboardingStepFrame";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  useCreateIncidentType,
  useDeleteIncidentType,
  useUpdateIncidentType,
} from "@/lib/data/react-query/matters/matter-type";
import { IncidentTypeSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { retrieveIncidentTypeCategoriesQueryOptions } from "@/lib/data/react-query/matters/matter-type-category";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type IncidentType } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { sortBy, toLower } from "lodash";
import { Check, Trash2 } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = IncidentTypeSchema.pick({
  label: true,
  description: true,
  incidentTypeCategoryId: true,
}).extend({ label: z.string().trim().min(1, "Label is required") });

const defaultFormValues: z.infer<typeof formSchema> = {
  label: "",
  description: "",
  incidentTypeCategoryId: "",
};

const inlineFieldSchemas = {
  label: z.string().trim().min(1, "Label is required"),
  description: IncidentTypeSchema.shape.description,
  incidentTypeCategoryId: IncidentTypeSchema.shape.incidentTypeCategoryId,
};

type AddIncidentTypeEditorProps = {
  incidentTypes: IncidentType[];
  isReadOnly: boolean;
  onClose: () => void;
};

function AddIncidentTypeEditor({
  incidentTypes,
  isReadOnly,
  onClose,
}: AddIncidentTypeEditorProps) {
  const { data: categoryData } = useQuery(
    retrieveIncidentTypeCategoriesQueryOptions(),
  );
  const incidentCategories = categoryData?.data ?? [];
  const [incidentTypeCategoryIdOpen, setClaimTypeOpen] = useState(false);
  const createIncidentType = useCreateIncidentType({ showToast: false });
  const isFormLoading = createIncidentType.isPending;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  useEffect(() => {
    form.reset(defaultFormValues);
  }, [form]);

  const createErrorMessage = useMemo(() => {
    const errors = [
      form.formState.errors.label?.message,
      form.formState.errors.description?.message,
      form.formState.errors.incidentTypeCategoryId?.message,
    ].filter(Boolean);

    return errors.length > 0 ? String(errors[0]) : null;
  }, [form.formState.errors]);

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      incidentTypes.some(
        (incidentType) =>
          toLower(incidentType.label ?? "") === toLower(data.label),
      )
    ) {
      form.setError("label", {
        message: "Incident type label must be unique",
      });
      return;
    }

    createIncidentType.mutate({
      ...data,
      emailModuleAccess: true,
      expenseModuleAccess: true,
      incidentModuleAccess: true,
      incidentReportModuleAccess: true,
      intakePropertyDamageModuleAccess: true,
      intakeResponsesModuleAccess: true,
      lienModuleAccess: true,
      litigationModuleAccess: true,
      lostWagesModuleAccess: true,
      medicalTreatmentModuleAccess: true,
      settlementModuleAccess: true,
      trustCheckModuleAccess: true,
      injuriesModuleAccess: true,
      insuranceModuleAccess: true,
      signatureRequestModuleAccess: true,
      vehicleModuleAccess: true,
      incidentTypeCategoryId: data.incidentTypeCategoryId || null,
    });
    onClose();
    form.reset(defaultFormValues);
  };

  return (
    <div>
      <Form {...form}>
        <form
          className="relative w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <fieldset
            disabled={isReadOnly || isFormLoading}
            className="bg-muted/60 flex w-full flex-col gap-2 rounded-xl px-2 py-1.5 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="New type label"
                          className="border-border/40 h-9 w-44 bg-white px-2 py-0.5 text-sm leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="min-w-0 space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          placeholder="Add description"
                          className="border-border/40 h-9 w-60 bg-white px-2 py-0.5 text-xs leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              {createErrorMessage ? (
                <Typography className="text-destructive text-xs">
                  {createErrorMessage}
                </Typography>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:justify-end">
              <FormField
                control={form.control}
                name="incidentTypeCategoryId"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <Popover
                      modal
                      open={incidentTypeCategoryIdOpen}
                      onOpenChange={setClaimTypeOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={incidentTypeCategoryIdOpen}
                          aria-label="Select incident category"
                          disabled={isReadOnly || isFormLoading}
                          className="group border-border/40 h-7 w-40 justify-between gap-2 bg-white px-2 py-0.5 text-xs leading-normal shadow-none focus:ring-0 focus:ring-offset-0"
                        >
                          <span
                            className={
                              field.value ? "truncate" : "text-muted-foreground"
                            }
                          >
                            {incidentCategories.find(
                              (category) => category.id === field.value,
                            )?.label ?? "Incident category"}
                          </span>
                          <SelectDropdownChevron />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="min-w-[200px] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandList>
                            {incidentCategories.map((option) => (
                              <CommandItem
                                key={option.id}
                                value={option.label}
                                onSelect={() => {
                                  field.onChange(option.id);
                                  setClaimTypeOpen(false);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${field.value === option.id ? "opacity-100" : "opacity-0"}`}
                                />
                                {option.label}
                              </CommandItem>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                variant="ghost"
                size="xs"
                disabled={isReadOnly || isFormLoading}
                aria-label="Save new incident type"
              >
                <Check />
              </Button>
            </div>
          </fieldset>

          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </Form>
    </div>
  );
}

function IncidentTypesListDangerous() {
  const [activeTypeId, setActiveTypeId] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<
    "label" | "description" | "incidentTypeCategoryId" | null
  >(null);
  const { data: categoryData } = useQuery(
    retrieveIncidentTypeCategoriesQueryOptions(),
  );
  const incidentCategories = categoryData?.data ?? [];
  const [incidentTypeCategoryIdDropdownOpenId, setClaimTypeDropdownOpenId] =
    useState<string | null>(null);
  const [inlineValue, setInlineValue] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteType, setPendingDeleteType] =
    useState<IncidentType | null>(null);
  const { hasActionPermission } = useUser();
  const { scrollToBottom } = useOnboardingStepFrameScroll();
  const previousIncidentTypeCount = useRef<number | null>(null);

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const incidentTypes = useMemo(
    () =>
      sortBy(matterSettingsData?.data.incidentTypes ?? [], (incidentType) =>
        new Date(incidentType.dateCreated ?? 0).getTime(),
      ),
    [matterSettingsData],
  );

  useEffect(() => {
    if (previousIncidentTypeCount.current === null) {
      previousIncidentTypeCount.current = incidentTypes.length;
      return;
    }

    if (incidentTypes.length > previousIncidentTypeCount.current) {
      scrollToBottom();
    }

    previousIncidentTypeCount.current = incidentTypes.length;
  }, [incidentTypes.length, scrollToBottom]);

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.incidentType,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.incidentType,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.incidentType,
    ACTION_PERMISSION_VALUES.delete,
  );

  const editIncidentType = useUpdateIncidentType({ showToast: false });
  const deleteIncidentType = useDeleteIncidentType({ showToast: false });

  const closeEditor = () => {
    setActiveTypeId(null);
    setActiveField(null);
    setClaimTypeDropdownOpenId(null);
    setInlineValue("");
    setInlineError(null);
  };

  const startInlineEdit = (
    incidentType: IncidentType,
    field: "label" | "description" | "incidentTypeCategoryId",
  ) => {
    if (!canUpdate) return;
    setActiveTypeId(incidentType.id);
    setActiveField(field);
    setInlineValue(
      field === "label"
        ? (incidentType.label ?? "")
        : field === "description"
          ? (incidentType.description ?? "")
          : (incidentType.incidentTypeCategoryId ?? ""),
    );
    setInlineError(null);
  };

  const openClaimTypeEditor = (incidentType: IncidentType) => {
    if (!canUpdate) return;
    closeEditor();
    setActiveTypeId(incidentType.id);
    setActiveField("incidentTypeCategoryId");
    setInlineValue(incidentType.incidentTypeCategoryId ?? "");
    setClaimTypeDropdownOpenId(incidentType.id);
  };

  const submitClaimTypeEdit = (
    incidentType: IncidentType,
    nextValue: string,
  ) => {
    const validation =
      inlineFieldSchemas.incidentTypeCategoryId.safeParse(nextValue);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return;
    }

    if (validation.data === incidentType.incidentTypeCategoryId) {
      closeEditor();
      return;
    }

    editIncidentType.mutate({
      id: incidentType.id,
      dateModified: incidentType.dateModified,
      label: incidentType.label,
      description: incidentType.description ?? undefined,
      incidentTypeCategoryId: validation.data,
    });
    closeEditor();
  };

  const submitInlineEdit = (incidentType: IncidentType) => {
    if (!activeField) return;
    if (!canUpdate) {
      closeEditor();
      return;
    }

    const validation = inlineFieldSchemas[activeField].safeParse(inlineValue);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return;
    }

    const nextValue = validation.data;
    const currentValue =
      activeField === "label"
        ? (incidentType.label ?? "")
        : activeField === "description"
          ? (incidentType.description ?? "")
          : incidentType.incidentTypeCategoryId;

    if (nextValue === currentValue) {
      closeEditor();
      return;
    }

    if (
      activeField === "label" &&
      incidentTypes.some(
        (type) =>
          toLower(type.label ?? "") === toLower(nextValue as string) &&
          type.id !== incidentType.id,
      )
    ) {
      setInlineError("Incident type label must be unique");
      return;
    }

    editIncidentType.mutate({
      id: incidentType.id,
      dateModified: incidentType.dateModified,
      label:
        activeField === "label" ? (nextValue as string) : incidentType.label,
      description:
        activeField === "description"
          ? (nextValue as string)
          : (incidentType.description ?? undefined),
      incidentTypeCategoryId:
        activeField === "incidentTypeCategoryId"
          ? nextValue
          : incidentType.incidentTypeCategoryId,
    });
    closeEditor();
  };

  const handleInlineKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    incidentType: IncidentType,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitInlineEdit(incidentType);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeEditor();
    }
  };

  return (
    <div className="space-y-5">
      <ActionConfirmationModal
        title={`Are you sure you want to delete this incident type?`}
        description={`If this incident type is linked to any other items in settings, the link will be removed. \n\n If this incident type is connected to any matters, this deletion will be blocked.`}
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        afterActionFn={async () => {
          if (!pendingDeleteType) return;
          await deleteIncidentType.mutateAsync({
            id: pendingDeleteType.id,
          });
          setDeleteModalOpen(false);
          setPendingDeleteType(null);
        }}
        onCancel={() => {
          setPendingDeleteType(null);
        }}
      />

      {isMatterSettingsLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      ) : isMatterSettingsError ? (
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      ) : incidentTypes.length === 0 ? (
        <div className="space-y-2">
          <div className="border-border bg-muted/40 rounded-2xl border border-dashed p-6 text-center">
            <Typography weight="semibold">No incident types yet</Typography>
            <Typography className="text-muted-foreground text-sm">
              Add your first incident type to start organizing matters.
            </Typography>
          </div>
          {canCreate ? (
            <AddIncidentTypeEditor
              incidentTypes={incidentTypes}
              isReadOnly={!canUpdate}
              onClose={closeEditor}
            />
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          {incidentTypes.map((incidentType) => {
            const isEditingLabel =
              activeTypeId === incidentType.id && activeField === "label";
            const isEditingDescription =
              activeTypeId === incidentType.id && activeField === "description";
            const incidentTypeCategoryIdLabel =
              incidentType.incidentTypeCategoryId?.trim()
                ? (incidentCategories.find(
                    (category) =>
                      category.id === incidentType.incidentTypeCategoryId,
                  )?.label ?? incidentType.incidentTypeCategoryId.trim())
                : "No incident category";

            return (
              <div
                key={incidentType.id}
                className="hover:bg-muted/30 focus-visible:ring-primary flex flex-col gap-2 rounded-xl px-2 py-1.5 transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <Typography
                    tag="h4"
                    weight="semibold"
                    className="flex items-center gap-2 text-sm"
                  >
                    <span className="text-foreground flex-none">
                      <InlineEditableField
                        isEditing={isEditingLabel}
                        value={inlineValue}
                        displayValue={getIncidentTypeLabel(incidentType)}
                        canEdit={canUpdate}
                        ariaLabel="Incident type label"
                        inputWidthClass="w-auto max-w-full"
                        autoSize
                        matchDisplaySize
                        onStartEdit={() => {
                          startInlineEdit(incidentType, "label");
                        }}
                        onChange={(value: string) => {
                          setInlineValue(value);
                          if (inlineError) setInlineError(null);
                        }}
                        onSubmit={() => {
                          submitInlineEdit(incidentType);
                        }}
                        onKeyDown={(
                          event: KeyboardEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {
                          handleInlineKeyDown(event, incidentType);
                        }}
                      />
                    </span>
                    <span className="text-muted-foreground min-w-0 flex-1 text-xs leading-snug">
                      <InlineEditableField
                        isEditing={isEditingDescription}
                        value={inlineValue}
                        displayValue={
                          incidentType.description
                            ? incidentType.description
                            : "No description"
                        }
                        canEdit={canUpdate}
                        ariaLabel="Incident type description"
                        inputWidthClass="w-auto max-w-full"
                        autoSize
                        textClassName="text-xs"
                        matchDisplaySize
                        onStartEdit={() => {
                          startInlineEdit(incidentType, "description");
                        }}
                        onChange={(value: string) => {
                          setInlineValue(value);
                          if (inlineError) setInlineError(null);
                        }}
                        onSubmit={() => {
                          submitInlineEdit(incidentType);
                        }}
                        onKeyDown={(
                          event: KeyboardEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                        ) => {
                          handleInlineKeyDown(event, incidentType);
                        }}
                      />
                    </span>
                  </Typography>
                  {activeTypeId === incidentType.id && inlineError ? (
                    <Typography className="text-destructive text-xs">
                      {inlineError}
                    </Typography>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 sm:flex-nowrap sm:justify-end">
                  <div className="flex flex-wrap items-center gap-2">
                    <Popover
                      modal
                      open={
                        incidentTypeCategoryIdDropdownOpenId === incidentType.id
                      }
                      onOpenChange={(nextOpen) => {
                        if (nextOpen) {
                          openClaimTypeEditor(incidentType);
                          return;
                        }
                        closeEditor();
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={
                            incidentTypeCategoryIdDropdownOpenId ===
                            incidentType.id
                          }
                          aria-label="Select incident category"
                          disabled={!canUpdate}
                          className="group border-border/40 h-7 w-40 justify-between gap-2 bg-white px-2 py-0.5 text-xs leading-normal shadow-none focus:ring-0 focus:ring-offset-0"
                        >
                          <span className="truncate">
                            {incidentTypeCategoryIdLabel}
                          </span>
                          <SelectDropdownChevron />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="min-w-[200px] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandList>
                            {incidentCategories.map((option) => (
                              <CommandItem
                                key={option.id}
                                value={option.label}
                                onSelect={() => {
                                  if (inlineError) setInlineError(null);
                                  submitClaimTypeEdit(incidentType, option.id);
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${incidentType.incidentTypeCategoryId === option.id ? "opacity-100" : "opacity-0"}`}
                                />
                                {option.label}
                              </CommandItem>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={!canDelete}
                      onClick={() => {
                        setPendingDeleteType(incidentType);
                        setDeleteModalOpen(true);
                      }}
                      aria-label={`Delete ${getIncidentTypeLabel(incidentType)}`}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {canCreate ? (
            <AddIncidentTypeEditor
              incidentTypes={incidentTypes}
              isReadOnly={!canUpdate}
              onClose={closeEditor}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

export function IncidentTypesList() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <IncidentTypesListDangerous />
    </ErrorBoundary>
  );
}
